import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "row" }
const _hoisted_2 = { class: "row gap-02" }

import { useI18n } from "vue-i18n";
import { computed, ref, watch } from "vue";
import { useAppUserStore } from "@/stores/appUser";
import esTransportStopForm from "@/components/form/esTransportStopForm.vue";
import EsConfirmDialog from '@/components/modals/esConfirmDialog.vue';
import { ApiError, PersonApi, TransportStop } from "@/apiGenerate/medserver";
import { medServerApiConfig } from "@/config/medserver-api.config";
import { Column, DataTable, useToast } from "primevue";
import { toastError, toastSuccess } from "@/utils/toast-utils";


export default /*@__PURE__*/_defineComponent({
  __name: 'esTransportStopSettings',
  setup(__props) {

const { t } = useI18n();
const appUserStore = useAppUserStore();
const toast = useToast();

const dropd = ref("");
const isConfirmModalOpened = ref(false);
const isFormModalOpened = ref(false);
const transportStops = ref<TransportStop[]>([]);
const currentStop = ref<TransportStop>({
  doctorUuid : '',
  uuid: '',
  stop: ''
})
const personApi = new PersonApi(medServerApiConfig);
const pwr = appUserStore.currentHealthGroupMembership?.pwr;

/**
 * Opens the add transport stop form modal with an empty stop object.
 */
const openAddForm = () => {
  currentStop.value = {
    doctorUuid : pwr.personWithRoleId,
    uuid: '',
    stop: ''
  };
  isFormModalOpened.value = true;
};

/**
 * Opens the update transport stop form modal with the given stop data.
 * @param stop - The transport stop data to update.
 */
const openUpdateForm = (stop: TransportStop) => {
  dropd.value = "";
  currentStop.value = { ...stop };
  isFormModalOpened.value = true;
};

const onTransportStopModalClose = () => {
  isFormModalOpened.value = false;
}

/**
 * Closes the transport stop form modal and either updates or creates a stop
 * depending on the form's edit state.
 * @param formData - The submitted form data, including confirmation and editing flags.
 */
const onTransportStopModalConfirm = async (transportStop: TransportStop) => {
  if (!pwr) return;

  let request: () => Promise<TransportStop>;
    if (transportStop.uuid) {
      request = () => personApi.updateTransportStop({
        doctorId: pwr.personWithRoleId,
        transportId: transportStop.uuid,
        createTransportStop: { name: transportStop.stop }
      });
    } else {
      request = () => personApi.createTransportStop({
          doctorId: pwr.personWithRoleId,
          createTransportStop: { name: transportStop.stop }
      });
    }

    await request()
    .then(() => {
      toastSuccess(toast, {
        summary: t('common.toast.success.update'),
        detail: t('settings.params.account.toast.update.information'),
      });
      getAllStop();
      isFormModalOpened.value = false;
    })
    .catch((error: ApiError) => {
      toastError(toast, {
        summary: t('common.toast.error'),
        detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
      });

      // Throw error to set at false the var __alreadyClicked in v-one-click directive
      throw error;
    });
};

/**
 * Closes the delete transport stop confirmation modal and deletes the stop entry if confirmed.
 * @param confirm - Whether the deletion was confirmed.
 */
const closeConfirmModal = async (confirm: boolean) => {
  if (!pwr) return;

  if (confirm) {
    await personApi.deleteDoctorTransportStop({ doctorId: pwr.personWithRoleId, transportId: currentStop.value.uuid })
      .then(() => {
        toastSuccess(toast, {
          summary: t('common.toast.success.update'),
          detail: t('settings.params.account.toast.delete.stop'),
        });
        getAllStop();
        isConfirmModalOpened.value = false;
      })
      .catch((error: ApiError) => {
        toastError(toast, {
          summary: t('common.toast.error'),
          detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
        });

        // Throw error to set at false the var __alreadyClicked in v-one-click directive
        throw error;
      });
  } else {
    isConfirmModalOpened.value = false;
  }
};

/**
 * Opens the delete transport stop confirmation modal for the given stop.
 * @param stop - The transport stop entry to delete.
 */
const openConfirmModal = (stop: TransportStop) => {
  dropd.value = "";
  currentStop.value = { ...stop };
  isConfirmModalOpened.value = true;
};

/**
 * Fetches all transport stops for the current doctor and updates the reactive list.
 */
const getAllStop = async () => {  
  await personApi.getTransportStops({
    doctorId: appUserStore.currentHealthGroupMembership?.pwr.personWithRoleId
  })
  .then((response: TransportStop[]) => {
    transportStops.value = response;
  })
  .catch((error: ApiError) => {
    toastError(toast, {
      summary: t('common.toast.error'),
      detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
    });
  });
};

/**
 * Watches for changes to the current institute UUID
 * and triggers the transport stop retrieval.
 */
const currInstituteUUID = computed(() => {
  if (!appUserStore.currentHealthGroupMembership) {return "";}
  return appUserStore.currentHealthGroupMembership.instituteWithRole.instituteWithRoleId || "";
});

/**
 * Transport stop form modal header key, changes depending on add or update mode.
 */
const stopHeaderKey = computed(() =>
    currentStop.value?.uuid
        ? t('settings.params.account.institutes.portailPatient.transportStop.update')
        : t('settings.params.account.institutes.portailPatient.transportStop.add')
)

watch(currInstituteUUID, async (newVal) => {
  if (newVal) {
    await getAllStop();
  }
}, { immediate: true });

return (_ctx: any,_cache: any) => {
  const _component_svg_item = _resolveComponent("svg-item")!
  const _component_text_item = _resolveComponent("text-item")!
  const _component_buttonGroup = _resolveComponent("buttonGroup")!
  const _component_es_button_icon = _resolveComponent("es-button-icon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_component_buttonGroup, {
        onClick: _cache[0] || (_cache[0] = ($event: any) => (openAddForm()))
      }, {
        default: _withCtx(() => [
          _createVNode(_component_svg_item, { icon: "plus" }),
          _createVNode(_component_text_item, {
            txt: _unref(t)('action.add'),
            styles: "font-mBold"
          }, null, 8, ["txt"])
        ]),
        _: 1
      })
    ]),
    (transportStops.value && transportStops.value.length > 0)
      ? (_openBlock(), _createBlock(_unref(DataTable), {
          key: 0,
          value: transportStops.value,
          "data-key": "uuid",
          "striped-rows": "",
          class: "mtop-06"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(Column), {
              field: "stop",
              header: _unref(t)('settings.label.instituteInformation.name'),
              style: {"min-width":"50rem"},
              class: "table-main-col"
            }, null, 8, ["header"]),
            _createVNode(_unref(Column), {
              field: "actions",
              header: _unref(t)('settings.label.instituteInformation.action')
            }, {
              body: _withCtx(({ data }) => [
                _createElementVNode("div", _hoisted_2, [
                  _createVNode(_component_es_button_icon, {
                    icon: "pencil_fill",
                    folder: "shareIcons",
                    class: "pad-l",
                    styles: "img-primary",
                    onClick: ($event: any) => (openUpdateForm(data))
                  }, null, 8, ["onClick"]),
                  _createVNode(_component_es_button_icon, {
                    icon: "trash",
                    folder: "shareIcons",
                    class: "pad-l",
                    styles: "img-primary",
                    onClick: ($event: any) => (openConfirmModal(data))
                  }, null, 8, ["onClick"])
                ])
              ]),
              _: 1
            }, 8, ["header"])
          ]),
          _: 1
        }, 8, ["value"]))
      : _createCommentVNode("", true),
    _createVNode(esTransportStopForm, {
      isOpen: isFormModalOpened.value,
      stop: currentStop.value,
      header: _unref(t)(stopHeaderKey.value),
      "confirm-method": onTransportStopModalConfirm,
      onModalClose: _cache[1] || (_cache[1] = ($event: any) => (onTransportStopModalClose()))
    }, null, 8, ["isOpen", "stop", "header"]),
    _createVNode(EsConfirmDialog, {
      isOpen: isConfirmModalOpened.value,
      onModalClose: _cache[2] || (_cache[2] = () => closeConfirmModal(false)),
      header: _unref(t)('settings.params.account.institutes.portailPatient.transportStop.delete'),
      message: _unref(t)('settings.params.account.institutes.portailPatient.transportStop.message'),
      "on-confirm": () => closeConfirmModal(true)
    }, null, 8, ["isOpen", "header", "message", "on-confirm"])
  ], 64))
}
}

})